import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Empty, Input, Row, Space, Table, Tag, Typography, message } from "antd";
import { ReloadOutlined, SearchOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import { getEmployeesReq } from "../../employeeManagement/companyEmployees/requests";
import { getResourceDataReq } from "@comps/commonFunctions";
import UpdatePayrollPolicy from "../../employeeManagement/companyEmployees/components/UpdatePayrollPolicy";

const { Text } = Typography;

const PayrollEmployeePolicyAssignment = (props) => {
  const [loader, setLoader] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmploymentId, setSelectedEmploymentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    current: 1,
    pageSize: 10,
  });
  const [searchText, setSearchText] = useState("");
  const [quickMatches, setQuickMatches] = useState([]);

  useEffect(() => {
    getEmployees();
    // eslint-disable-next-line
  }, [currentPage]);

  const getEmployees = () => {
    const payload = {
      page: currentPage,
      filters: ";employee_status_|active",
      showActive: true,
    };

    makeRequest(setLoader, getEmployeesReq, payload, onEmployeesSuccess, onError);
  };

  const onEmployeesSuccess = (response) => {
    const list = response?.employee_employments?.data || [];
    const meta = response?.employee_employments?.meta?.pagination || {};

    setEmployees(list);
    setPagination({
      total: meta.total || 0,
      current: meta.current_page || currentPage,
      pageSize: meta.per_page || 10,
    });

    setSelectedEmploymentId((current) => current || list[0]?.id || null);
  };

  const onError = (err, res) => {
    message.error(res?.response?.data?.message || "Unable to load employees");
  };

  const onQuickSearch = (value) => {
    const query = value.trim();
    setSearchText(query);

    if (!query) {
      setQuickMatches([]);
      return;
    }

    const payload = {
      page: 1,
      type: "employment",
      filters: `search_|${query};`,
    };

    makeRequestStateless(getResourceDataReq, payload, (response) => {
      setQuickMatches(response?.resources?.data || []);
    }, () => {
      setQuickMatches([]);
    });
  };

  const filteredEmployees = useMemo(() => {
    if (!searchText) {
      return employees;
    }

    const keyword = searchText.toLowerCase();
    return employees.filter((employee) => (
      `${employee.full_name_code || ""} ${employee.branch_name || ""} ${employee.department_name || ""} ${employee.designation_name || ""}`
        .toLowerCase()
        .includes(keyword)
    ));
  }, [employees, searchText]);

  const columns = [
    {
      title: "Employee",
      key: "employee",
      render: (record) => (
        <div>
          <div style={ { fontWeight: 600, color: "#111827" } }>{ record.full_name_code }</div>
          <Text type="secondary">{ record.designation_name || "No designation" }</Text>
        </div>
      ),
    },
    {
      title: "Branch",
      dataIndex: "branch_name",
      key: "branch_name",
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
    },
    {
      title: "Action",
      key: "action",
      render: (record) => (
        <Button
          type={ selectedEmploymentId === record.id ? "primary" : "default" }
          icon={ <UserSwitchOutlined /> }
          onClick={ () => setSelectedEmploymentId(record.id) }
        >
          { selectedEmploymentId === record.id ? "Managing" : "Manage policy" }
        </Button>
      ),
    },
  ];

  const selectedEmployee = employees.find((employee) => employee.id === selectedEmploymentId)
    || quickMatches.find((employee) => employee.id === selectedEmploymentId);

  return (
    <div className="payroll-workspace__section">
      <Row gutter={ [16, 16] }>
        <Col xs={ 24 } xl={ 10 }>
          <Card
            bordered={ false }
            className="payroll-workspace__detail-card"
            title="Assign policy to employee"
            extra={ <Button icon={ <ReloadOutlined /> } onClick={ getEmployees }>Refresh</Button> }
          >
            <Space direction="vertical" size={ 12 } style={ { width: "100%" } }>
              <Input
                prefix={ <SearchOutlined /> }
                placeholder="Search loaded employees or press Enter for quick lookup"
                value={ searchText }
                onChange={ (event) => setSearchText(event.target.value) }
                onPressEnter={ (event) => onQuickSearch(event.target.value) }
              />

              { quickMatches.length > 0 && (
                <Card size="small" style={ { background: "#f8fafc", borderRadius: 12 } }>
                  <Space wrap>
                    { quickMatches.slice(0, 8).map((employee) => (
                      <Button key={ employee.id } onClick={ () => setSelectedEmploymentId(employee.id) }>
                        { employee.full_name_code || employee.identity || employee.full_name }
                      </Button>
                    )) }
                  </Space>
                </Card>
              ) }

              <Table
                rowKey="id"
                size="small"
                columns={ columns }
                dataSource={ filteredEmployees }
                loading={ loader }
                pagination={ {
                  total: pagination.total,
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  onChange: setCurrentPage,
                } }
                locale={ {
                  emptyText: <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No employees available" />,
                } }
              />
            </Space>
          </Card>
        </Col>

        <Col xs={ 24 } xl={ 14 }>
          <Card
            bordered={ false }
            className="payroll-workspace__detail-card"
            title="Employee payroll and policy"
            extra={ selectedEmployee ? <Tag color="blue">{ selectedEmployee.full_name_code || selectedEmployee.identity }</Tag> : null }
          >
            { selectedEmploymentId ? (
              <UpdatePayrollPolicy key={ selectedEmploymentId } id={ selectedEmploymentId } />
            ) : (
              <Empty description="Select an employee to apply payroll policy." image={ Empty.PRESENTED_IMAGE_SIMPLE } />
            ) }
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PayrollEmployeePolicyAssignment;
